import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import api from "../api";
import Logo from "../components/Logo";
import ProfileMenu from "../components/ProfileMenu";
import {
  LayoutGrid, Shield, Phone, ArrowLeft, ExternalLink, Loader2, RefreshCw, AlertTriangle,
} from "lucide-react";

const PRODUCT = {
  aegis: { label: "Aegis", icon: Shield, color: "var(--au-accent-ink)" },
  sip: { label: "SIP", icon: Phone, color: "var(--au-info)" },
};
// risk: high=red/bad; health: high=green/good
const scoreColor = (kind, band) => {
  if (band == null) return "var(--au-muted)";
  if (kind === "risk") return band === "high" ? "var(--au-error)" : band === "medium" ? "var(--au-warn)" : "var(--au-accent-ink)";
  return band === "good" ? "var(--au-success)" : band === "fair" ? "var(--au-warn)" : "var(--au-error)";
};

export default function Activity() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(false);

  const load = async () => {
    setError(false); setBusy(true);
    try { const r = await api.get("/api/activity"); setData(r.data); }
    catch (e) { setError(true); toast.error("Couldn't load activity"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  if (busy) return <div className="au-root" style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "center", justifyContent: "center", color: "var(--au-accent-ink)", fontFamily: "var(--au-font-mono)" }}><Loader2 size={28} className="au-spin" /><span>Loading…</span></div>;
  if (error) return (
    <div className="au-root" style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div className="au-glass au-pad-lg" style={{ textAlign: "center", maxWidth: 420 }}>
        <AlertTriangle size={40} style={{ color: "var(--au-warn)", marginBottom: 14 }} />
        <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--au-text)", marginBottom: 8 }}>Couldn't load activity</h2>
        <button onClick={load} className="au-pill au-pill-primary au-pill-sm"><RefreshCw size={14} /> Retry</button>
      </div>
    </div>
  );

  const jobs = data?.jobs || [];
  const sum = data?.summary || {};

  return (
    <div className="au-root">
      <nav style={{ padding: "14px 28px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Link to="/" style={{ textDecoration: "none" }}><Logo size="small" /></Link>
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          <Link to="/dashboard" style={{ color: "var(--au-text-2)", textDecoration: "none", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}><ArrowLeft size={14} /> Dashboard</Link>
          <ProfileMenu />
        </div>
      </nav>

      <div style={{ maxWidth: 1000, margin: "0 auto", padding: "32px 24px" }}>
        <h1 style={{ fontSize: 26, fontWeight: 700, color: "var(--au-text)", letterSpacing: 1.5, marginBottom: 6, display: "flex", alignItems: "center", gap: 12 }}>
          <LayoutGrid size={24} style={{ color: "var(--au-accent-ink)" }} /> Unified Activity
        </h1>
        <p style={{ color: "var(--au-muted)", marginBottom: 24, fontSize: 14 }}>Every job across Aegis and SIP in one place</p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, marginBottom: 22 }}>
          <Stat label="Total jobs" value={sum.total || 0} />
          <Stat label="Aegis scans" value={sum.aegis || 0} color="var(--au-accent-ink)" />
          <Stat label="SIP tests" value={sum.sip || 0} color="var(--au-info)" />
          <Stat label="High risk" value={sum.high_risk || 0} color="var(--au-error)" />
        </div>

        <div className="au-glass" style={{ overflow: "hidden" }}>
          <div style={{ padding: "16px 22px", borderBottom: "1px solid var(--au-hair)", fontSize: 14, fontWeight: 700, color: "var(--au-text)", letterSpacing: 1 }}>RECENT ACTIVITY</div>
          {jobs.length === 0 ? (
            <div style={{ padding: 48, textAlign: "center", color: "var(--au-muted)" }}>No activity yet.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <div style={{ minWidth: 640 }}>
                <div style={{ display: "grid", gridTemplateColumns: "110px 2fr 1fr 1fr 1.2fr", padding: "10px 22px", fontSize: 10, color: "var(--au-muted)", letterSpacing: 2, textTransform: "uppercase", borderBottom: "1px solid var(--au-hair)" }}>
                  <span>Product</span><span>Target</span><span>Score</span><span>When</span><span></span>
                </div>
                {jobs.map((j) => {
                  const P = PRODUCT[j.product] || { label: j.product, icon: LayoutGrid, color: "var(--au-muted)" };
                  const Icon = P.icon;
                  return (
                    <div key={j.product + j.id} style={{ display: "grid", gridTemplateColumns: "110px 2fr 1fr 1fr 1.2fr", padding: "13px 22px", borderBottom: "1px solid var(--au-hair)", alignItems: "center" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: P.color }}><Icon size={14} /> {P.label}</span>
                      <span className="au-mono" style={{ fontSize: 13, color: "var(--au-text-2)", wordBreak: "break-all" }}>{j.target}</span>
                      <span className="au-mono" style={{ fontSize: 16, fontWeight: 700, color: scoreColor(j.score_kind, j.score_band) }}>
                        {j.score != null ? j.score : "—"}
                        <span style={{ fontSize: 9, color: "var(--au-muted)", marginLeft: 4, letterSpacing: 1 }}>{j.score_kind === "risk" ? "RISK" : "HEALTH"}</span>
                      </span>
                      <span style={{ fontSize: 11, color: "var(--au-muted)" }}>{j.created_at ? new Date(j.created_at).toLocaleDateString() : "—"}</span>
                      <span>
                        {j.link
                          ? <a href={j.link} target="_blank" rel="noreferrer" className="au-pill au-pill-glass au-pill-sm" style={{ padding: "4px 10px", fontSize: 11, textDecoration: "none" }}><ExternalLink size={12} /> Report</a>
                          : <span style={{ fontSize: 11, color: "var(--au-muted)" }}>—</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="au-glass" style={{ padding: "14px 18px" }}>
      <div className="au-stat-lbl" style={{ letterSpacing: 2, marginBottom: 6 }}>{label}</div>
      <div className="au-mono" style={{ fontSize: 24, fontWeight: 700, color: color || "var(--au-text)" }}>{value}</div>
    </div>
  );
}
